import React from 'react';
import { useTranslation } from 'react-i18next';
import { trpc } from '@org/clients/school';
import { Badge, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, StatTile, type ChartSeries, type TimePoint } from '@org/ui';
import { useAnalytics } from '../analytics-context';
import { ReportCard } from '../report-card';
import { ReportTable } from '../report-table';
import { TimeSeriesCard } from '../time-series-card';
import { formatDate, formatRate } from '../format';

type RiskFilter = 'high' | 'medium' | 'all';
const RISK_VARIANT = { high: 'destructive', medium: 'secondary', low: 'outline' } as const;

export function RiskReports() {
  const { t, i18n } = useTranslation('admin');
  const { input } = useAnalytics();
  const lang = i18n.language;
  const risk = trpc.analytics.risk.students.useQuery(input);
  const segments = trpc.analytics.risk.engagementSegments.useQuery(input);
  const [riskFilter, setRiskFilter] = React.useState<RiskFilter>('medium');
  const [streakMin, setStreakMin] = React.useState<2 | 3 | 4>(2);

  const students = risk.data?.students ?? [];
  const atRisk = students.filter((s) => (riskFilter === 'all' ? true : riskFilter === 'high' ? s.level === 'high' : s.level !== 'low'));
  const streaks = students.filter((s) => s.streak >= streakMin).sort((a, b) => b.streak - a.streak);

  const segmentSeries: ChartSeries[] = [
    { key: 'high', label: t('analytics.risk.segments.high') },
    { key: 'medium', label: t('analytics.risk.segments.medium') },
    { key: 'low', label: t('analytics.risk.segments.low') },
    { key: 'inactive', label: t('analytics.risk.segments.inactive'), color: '#b8b7b2' },
  ];
  const moveSeries: ChartSeries[] = [
    { key: 'movedDown', label: t('analytics.risk.segments.movedDown'), color: '#e34948' },
    { key: 'movedUp', label: t('analytics.risk.segments.movedUp'), color: '#008300' },
  ];
  const segmentData: TimePoint[] = (segments.data?.series ?? []).map((p) => ({
    bucket: p.bucket,
    high: p.high,
    medium: p.medium,
    low: p.low,
    inactive: p.inactive,
  }));
  const moveData: TimePoint[] = (segments.data?.series ?? []).map((p) => {
    const point: TimePoint = { bucket: p.bucket };
    if (p.movedDown !== null) point.movedDown = p.movedDown;
    if (p.movedUp !== null) point.movedUp = p.movedUp;
    return point;
  });

  const reasonText = (r: { code: string; value: number }) => t(`analytics.risk.reasons.${r.code}`, { value: r.value });

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <StatTile label={t('analytics.risk.tiles.high')} value={risk.data?.counts.high ?? null} hint={t('analytics.risk.tiles.highHint')} />
        <StatTile label={t('analytics.risk.tiles.medium')} value={risk.data?.counts.medium ?? null} hint={t('analytics.risk.tiles.mediumHint')} />
        <StatTile label={t('analytics.risk.tiles.low')} value={risk.data?.counts.low ?? null} />
        <StatTile label={t('analytics.risk.tiles.streak2')} value={risk.data?.counts.streak2 ?? null} />
        <StatTile label={t('analytics.risk.tiles.streak3')} value={risk.data?.counts.streak3 ?? null} />
      </div>

      <ReportCard
        title={t('analytics.risk.atRisk.title')}
        description={t('analytics.risk.atRisk.description')}
        isLoading={risk.isLoading}
        error={risk.error}
        isEmpty={students.length === 0}
        actions={
          <Select value={riskFilter} onValueChange={(v) => setRiskFilter(v as RiskFilter)}>
            <SelectTrigger className="h-8 w-[170px] text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="high">{t('analytics.risk.atRisk.filter.high')}</SelectItem>
              <SelectItem value="medium">{t('analytics.risk.atRisk.filter.medium')}</SelectItem>
              <SelectItem value="all">{t('analytics.risk.atRisk.filter.all')}</SelectItem>
            </SelectContent>
          </Select>
        }
      >
        <ReportTable
          rows={atRisk}
          rowKey={(r) => r.userId}
          defaultSort="score"
          pageSize={20}
          searchKeys={['name', 'email', 'groups']}
          columns={[
            { key: 'name', label: t('analytics.common.student'), value: (r) => r.name, className: 'font-medium' },
            { key: 'groups', label: t('analytics.filters.group'), value: (r) => r.groups, className: 'max-w-[220px] truncate text-muted-foreground' },
            {
              key: 'level',
              label: t('analytics.risk.atRisk.level'),
              value: (r) => r.score,
              render: (r) => <Badge variant={RISK_VARIANT[r.level]}>{t(`analytics.risk.levels.${r.level}`)}</Badge>,
            },
            { key: 'score', label: t('analytics.risk.atRisk.score'), value: (r) => r.score, align: 'right' },
            {
              key: 'reasons',
              label: t('analytics.risk.atRisk.why'),
              value: (r) => r.reasons.length,
              render: (r) => (
                <div className="flex flex-wrap gap-1">
                  {r.reasons.map((reason) => (
                    <span key={reason.code} className="rounded bg-muted px-1.5 py-0.5 text-[11px]">
                      {reasonText(reason)}
                    </span>
                  ))}
                </div>
              ),
            },
            { key: 'lastPresent', label: t('analytics.retention.recency.lastPresent'), value: (r) => r.lastPresentAt, render: (r) => formatDate(r.lastPresentAt, lang) },
            { key: 'attendance', label: t('analytics.risk.atRisk.attendance30'), value: (r) => r.recentAttendanceRate, render: (r) => formatRate(r.recentAttendanceRate), align: 'right' },
          ]}
        />
      </ReportCard>

      <ReportCard
        title={t('analytics.risk.streaks.title')}
        description={t('analytics.risk.streaks.description')}
        isLoading={risk.isLoading}
        error={risk.error}
        isEmpty={students.length === 0}
        actions={
          <Select value={String(streakMin)} onValueChange={(v) => setStreakMin(Number(v) as 2 | 3 | 4)}>
            <SelectTrigger className="h-8 w-[150px] text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[2, 3, 4].map((n) => (
                <SelectItem key={n} value={String(n)}>
                  {t('analytics.risk.streaks.min', { n })}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        }
      >
        <ReportTable
          rows={streaks}
          rowKey={(r) => r.userId}
          defaultSort="streak"
          pageSize={20}
          searchKeys={['name', 'group', 'teacher']}
          columns={[
            { key: 'name', label: t('analytics.common.student'), value: (r) => r.name, className: 'font-medium' },
            { key: 'group', label: t('analytics.filters.group'), value: (r) => r.lastAbsentGroup ?? r.groups },
            { key: 'teacher', label: t('analytics.common.teacher'), value: (r) => r.lastAbsentTeacher },
            { key: 'streak', label: t('analytics.risk.streaks.streak'), value: (r) => r.streak, align: 'right' },
            { key: 'lastAbsent', label: t('analytics.risk.streaks.lastAbsent'), value: (r) => r.lastAbsentAt, render: (r) => formatDate(r.lastAbsentAt, lang) },
            { key: 'lastPresent', label: t('analytics.retention.recency.lastPresent'), value: (r) => r.lastPresentAt, render: (r) => formatDate(r.lastPresentAt, lang) },
          ]}
        />
      </ReportCard>

      <TimeSeriesCard
        title={t('analytics.risk.segments.title')}
        description={t('analytics.risk.segments.description')}
        data={segmentData}
        series={segmentSeries}
        stacked
        isLoading={segments.isLoading}
        error={segments.error}
      />

      <TimeSeriesCard
        title={t('analytics.risk.segments.movesTitle')}
        description={t('analytics.risk.segments.movesDescription')}
        data={moveData}
        series={moveSeries}
        kind="line"
        isLoading={segments.isLoading}
        error={segments.error}
      />
    </div>
  );
}
